import { useMemo, useEffect, useRef } from 'react';
import { notifyAiReceptionistSettingsChanged } from '@frontend/ai-receptionist-ui';
import { Entries } from '@frontend/types';
import { theme } from '@frontend/theme';
import { FormConfig, FieldProps, FormValues, useForm, FieldStateReducer } from '@frontend/design-system';
import { useFormStateContext } from '../form-state-context';

export type FormWrapperChildrenProps<TFields extends FormConfig> = {
  getFieldProps: <G extends keyof TFields>(name: G) => FieldProps<TFields, G>;
  values: FormValues<TFields>;
  isValid: boolean;
  hasChangedValues: boolean;
  hasFormErrors: boolean;
};

type FormWrapperProps<TFields extends FormConfig> = {
  fields: TFields;
  children: (props: FormWrapperChildrenProps<TFields>) => React.ReactNode;
  onSubmit?: (args: { changedValues: FormValues<TFields>; values: FormValues<TFields> }) => void;
  initialValues?: FormValues<TFields>;
  customHasChanged?: Partial<{
    [K in keyof TFields]: (currentValue: any, initialValue: any) => boolean;
  }>;
  hasExternallyChangedValues?: boolean;
  locationId?: string;
  fieldStateReducer?: FieldStateReducer<TFields>;
};

export const FormWrapper = <TFields extends FormConfig>({
  fields,
  children,
  onSubmit,
  initialValues,
  customHasChanged,
  locationId,
  fieldStateReducer,
  hasExternallyChangedValues,
}: FormWrapperProps<TFields>) => {
  const { setFormState, clearFormState } = useFormStateContext();
  const mergedFields = initialValues
    ? (Object.entries(fields) as Entries<TFields>).reduce((acc, [key, field]) => {
        acc[key] = {
          ...field,
          value: initialValues[key as keyof typeof initialValues] ?? field.value,
        };
        return acc;
      }, {} as TFields)
    : fields;

  const handleSubmit = (formValues: FormValues<TFields>, changedValues: FormValues<TFields> | null) => {
    if (onSubmit && isComplete && (changedValues || hasExternallyChangedValues)) {
      onSubmit({ changedValues: changedValues ?? {}, values: formValues });
      // stop test call and show banner that settings have changed
      notifyAiReceptionistSettingsChanged();
    }
  };

  const { getFieldProps, values, isComplete, changedValues, formProps, getErrors, validate } = useForm({
    fields: mergedFields,
    computeChangedValues: true,
    onSubmit: handleSubmit,
    fieldStateReducer,
  });

  const hasChangedValues = useMemo(
    () =>
      Object.entries(changedValues || {}).reduce((hasChanges, [key, value]) => {
        let isChanged = false;
        const field = mergedFields[key];
        const initialValue = initialValues?.[key as keyof typeof initialValues];
        const currentValue = values[key];
        // Use custom hasChanged if provided
        if (customHasChanged?.[key]) {
          isChanged = customHasChanged[key](currentValue, initialValue);
        } else if (field?.type === 'checklist') {
          const initialArray = (initialValue || []) as string[];
          const currentArray = (currentValue || []) as string[];

          isChanged =
            initialArray.length !== currentArray.length ||
            initialArray.some((item) => !currentArray.includes(item)) ||
            currentArray.some((item) => !initialArray.includes(item));
        } else {
          isChanged = value !== initialValue;
        }

        return hasChanges || isChanged;
      }, false),
    [changedValues, initialValues, values, mergedFields, customHasChanged]
  );

  const hasFormErrors = useMemo(() => Object.keys(getErrors()).length > 0, [getErrors]);
  const initialValidated = useRef(false);
  useEffect(() => {
    if (hasFormErrors && !initialValidated.current) {
      validate();
      initialValidated.current = true;
    }
  }, [hasFormErrors, validate]);

  // Update form state in context when it changes
  useEffect(() => {
    if (locationId) {
      setFormState(locationId, { hasChangedValues });
    }
  }, [locationId, hasChangedValues, setFormState]);

  // Clear form state on unmount
  useEffect(() => {
    return () => {
      if (locationId) {
        clearFormState(locationId);
      }
    };
  }, [locationId, clearFormState]);

  return (
    <form {...formProps} style={{ position: 'relative' }}>
      {children({ getFieldProps, values, isValid: isComplete, hasChangedValues, hasFormErrors })}
    </form>
  );
};

const FormWrapperField = ({ children }: { children: React.ReactNode }) => {
  return (
    <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2), paddingBottom: theme.spacing(4) }}>
      {children}
    </div>
  );
};

FormWrapper.Field = FormWrapperField;
